/**
 * One arrow, and the two INDEPENDENT things that can be unknown about it.
 *
 * ── DIRECTION AND TARGET ARE SEPARATE AXES, ALWAYS ──────────────────────────
 *
 * Direction is a property of the VERB. Target resolution is a property of the
 * TARGET. They are decided by different data, they fail independently, and
 * collapsing them into one "confidence" field silently drops the single most
 * common case in the product.
 *
 * `nexusApi` is that case. Its action is one of a 62-member closed union with
 * 31 reads and 31 writes and zero ambiguity — so the verb is CERTAIN. But the
 * resource it acts on comes out of `parametersSetup` and is routinely chosen at
 * runtime — so the target is UNRESOLVED. A single field cannot say "I know
 * exactly what this does and not what it does it to", and a renderer given one
 * number either draws a confident arrow to nothing or throws the verb away.
 *
 * ── `toId` IS NULLABLE, NEVER OPTIONAL ──────────────────────────────────────
 *
 * A missing target is a STATE the consumer must handle, not a key it may
 * forget exists. `toId?: string` lets a component destructure it, get
 * `undefined`, and render a broken arrow with no compiler complaint. `toId:
 * string | null` forces the question to be answered at every read site.
 */
import { z } from "zod";

import { RoleSystemNodeTypeSchema } from "./system-node.types";

/**
 * What the workflow does to the thing at the other end.
 *
 * One word per meaning, and the set is deliberately small — a renderer draws a
 * different arrow per verb, so a verb nobody can draw is a verb nobody should
 * emit.
 */
export const SystemEdgeVerb = {
  /** Data flows target → workflow. The workflow does not change the target. */
  READS: "reads",
  /** Data flows workflow → target. The target's state changes. */
  WRITES: "writes",
  /** The workflow causes the target to run and do its own work. */
  INVOKES: "invokes",
  /** The workflow consumes the target as a credential or capability. Never dereferenced. */
  USES: "uses",
  /** The target STARTS the workflow. This is the one verb whose arrow points inward. */
  FEEDS: "feeds"
} as const;

export type SystemEdgeVerb = (typeof SystemEdgeVerb)[keyof typeof SystemEdgeVerb];

export const SYSTEM_EDGE_VERBS = Object.values(SystemEdgeVerb) as [
  SystemEdgeVerb,
  ...SystemEdgeVerb[]
];

export const SystemEdgeVerbSchema = z.enum(SYSTEM_EDGE_VERBS);

/** Which way the arrow points, relative to the workflow that owns the node. */
export type SystemEdgeDirection = "outbound" | "inbound";

/**
 * The verb decides the direction, exhaustively.
 *
 * A `Record<SystemEdgeVerb, …>` rather than an `if` chain, so adding a verb is
 * a compile error here until someone says which way it points. A renderer must
 * read this and never re-derive direction from the verb string.
 */
export const EDGE_VERB_DIRECTION: Record<SystemEdgeVerb, SystemEdgeDirection> = {
  [SystemEdgeVerb.READS]: "outbound",
  [SystemEdgeVerb.WRITES]: "outbound",
  [SystemEdgeVerb.INVOKES]: "outbound",
  [SystemEdgeVerb.USES]: "outbound",
  // The trigger case: a plugin trigger is FED BY its tool, so the tool starts
  // the workflow. Rendered as an arrow into the workflow, never out of it.
  [SystemEdgeVerb.FEEDS]: "inbound"
};

/**
 * How much the VERB is trusted. Nothing to do with the target.
 *
 * - `certain`  — the verb is fixed by the node type, or read from a literal in
 *                the node's own data (a `nexusApi` action). No inference took
 *                place.
 * - `derived`  — the verb was inferred from stored metadata that exists for
 *                this purpose but was written by something else. A
 *                CUSTOM_MANIFEST tool's HTTP method is the case: GET is a read
 *                because HTTP says so, not because anyone declared it.
 * - `unknown`  — no verb metadata exists anywhere in the pipeline. Render the
 *                edge UNDIRECTED. Do not guess: a wrong arrow is pixel-identical
 *                to a right one and is strictly worse than no arrow.
 */
export const EdgeDirectionConfidence = {
  CERTAIN: "certain",
  DERIVED: "derived",
  UNKNOWN: "unknown"
} as const;

export type EdgeDirectionConfidence =
  (typeof EdgeDirectionConfidence)[keyof typeof EdgeDirectionConfidence];

export const EDGE_DIRECTION_CONFIDENCES = Object.values(EdgeDirectionConfidence) as [
  EdgeDirectionConfidence,
  ...EdgeDirectionConfidence[]
];

export const EdgeDirectionConfidenceSchema = z.enum(EDGE_DIRECTION_CONFIDENCES);

/**
 * What the other end of the arrow actually IS. Nothing to do with the verb.
 *
 * - `instance`   — a concrete resource id. `toId` is non-null.
 * - `class`      — the edge names a TYPE, not a member of it. A `*.list` action
 *                  touches every collection and names none, so it is ONE arrow
 *                  to "collections", never N arrows to N collections. `toId` is
 *                  null and that is the correct answer, not a missing one.
 * - `unresolved` — an instance is meant, and which one is decided at runtime.
 *                  `toId` is null because the answer does not exist yet.
 *
 * `class` and `unresolved` both carry `toId: null` and mean opposite things, so
 * a consumer that branches on `toId === null` alone is wrong. Branch on this.
 */
export const EdgeTargetResolution = {
  INSTANCE: "instance",
  CLASS: "class",
  UNRESOLVED: "unresolved"
} as const;

export type EdgeTargetResolution = (typeof EdgeTargetResolution)[keyof typeof EdgeTargetResolution];

export const EDGE_TARGET_RESOLUTIONS = Object.values(EdgeTargetResolution) as [
  EdgeTargetResolution,
  ...EdgeTargetResolution[]
];

export const EdgeTargetResolutionSchema = z.enum(EDGE_TARGET_RESOLUTIONS);

/**
 * Why a target could not be pinned to an instance.
 *
 * Carried only when `targetResolution` is `unresolved`, so the UI can say
 * something true instead of "unknown".
 */
export const EdgeUnresolvedReason = {
  /** `ParameterSetup.handler === "variable"` — a prior node's output supplies it. */
  FROM_UPSTREAM_NODE: "from_upstream_node",
  /** `ParameterSetup.handler === "prompt"` — an LLM picks it at runtime. */
  CHOSEN_BY_MODEL: "chosen_by_model",
  /** A `manual` value carrying a `{{…}}` template, so the literal is not the id. */
  TEMPLATED_LITERAL: "templated_literal",
  /** The node names a target field that is absent or empty in its stored data. */
  TARGET_NOT_CONFIGURED: "target_not_configured"
} as const;

export type EdgeUnresolvedReason = (typeof EdgeUnresolvedReason)[keyof typeof EdgeUnresolvedReason];

export const EDGE_UNRESOLVED_REASONS = Object.values(EdgeUnresolvedReason) as [
  EdgeUnresolvedReason,
  ...EdgeUnresolvedReason[]
];

export const EdgeUnresolvedReasonSchema = z.enum(EDGE_UNRESOLVED_REASONS);

/** One derived arrow. Pure data — no ids the caller did not supply, no clock. */
export const DerivedEdgeSchema = z.object({
  /** The workflow node this arrow leaves from, as stored in `WorkflowNode.id`. */
  fromNodeId: z.string(),
  /** The raw `type` string off the node. NOT narrowed — production carries types the enum lacks. */
  fromNodeType: z.string(),
  /**
   * Nullable, never optional — and null EXACTLY when `directionConfidence` is
   * `unknown`.
   *
   * A Pipedream-backed plugin edge is real and has no verb: no verb metadata
   * exists anywhere in that pipeline. The alternative was a neutral `touches`
   * verb, and it is worse — it would be a SECOND field saying "direction
   * unknown", free to disagree with `directionConfidence`. A null cannot
   * disagree, and `derive-workflow-edges.test.ts` pins the biconditional in
   * both directions.
   */
  verb: SystemEdgeVerbSchema.nullable(),
  toType: RoleSystemNodeTypeSchema,
  /** Nullable, never optional. Null whenever resolution is `class` or `unresolved`. */
  toId: z.string().nullable(),
  directionConfidence: EdgeDirectionConfidenceSchema,
  targetResolution: EdgeTargetResolutionSchema,
  /** Present only when `targetResolution === "unresolved"`. Nullable, never optional. */
  unresolvedReason: EdgeUnresolvedReasonSchema.nullable()
});

export type DerivedEdge = z.infer<typeof DerivedEdgeSchema>;

/**
 * The direction an edge renders in, or `null` when it has none.
 *
 * Read from the VERB through the exhaustive map, never re-derived from the verb
 * string at a call site.
 */
export function edgeDirection(edge: DerivedEdge): SystemEdgeDirection | null {
  return edge.verb === null ? null : EDGE_VERB_DIRECTION[edge.verb];
}

/**
 * Whether this arrow may be drawn with a head.
 *
 * The one place a renderer should ask the question, so "unknown direction means
 * undirected" is stated once rather than re-derived at every call site. A wrong
 * arrow is pixel-identical to a right one and strictly worse than no arrow, so
 * a renderer that skips this check is not obviously broken — which is exactly
 * why the check lives here and not in the renderer.
 */
export function isDirected(edge: DerivedEdge): boolean {
  return edge.directionConfidence !== EdgeDirectionConfidence.UNKNOWN;
}
